import { RowActions, type RowActionHandlers } from './RowActions'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatDateForLocale } from '@/lib/dateUtils'
import type { RecoverRequest } from '../api'
import { useWorkforce } from './api'
import { useCandidates } from './useCandidates'
import { CandidateCard } from './CandidateCard'
import { accessRequirement, byDependencyOrder } from './logic'
import { effectiveIsGo } from '../components/WaitGoState'

/**
 * The queue in the order the estate actually recovers: network foundation first, then up the stack.
 *
 * The default view sorts by classification band already; what this adds is the two things that
 * decide whether a row can be handed to anyone at all — whether it needs somebody standing in
 * front of it, and who that could be — without leaving the table.
 */

function AccessChip({ access, inferred, source }: {
  access: 'on-site' | 'remote'; inferred: boolean; source: 'classification' | 'runbook' | 'name'
}) {
  const { t } = useTranslation()
  const onSite = access === 'on-site'
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium',
        inferred && 'border border-dashed')}
      title={source === 'classification'
        ? t('assignment.accessFromClassification', 'Set on the classification')
        : source === 'runbook'
          ? t('assignment.accessFromRunbook', 'Classification not set — read from the runbook steps')
          : t('assignment.accessInferred', 'Classification not set and no runbook — inferred from the object name')}
      style={onSite
        ? { backgroundColor: 'rgba(245,158,11,0.16)', color: 'rgb(146,88,8)' }
        : { backgroundColor: 'rgba(56,189,248,0.16)', color: 'rgb(3,105,161)' }}
    >
      {onSite
        ? <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>
        : <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="14" rx="2" /><path d="M8 21h8" /></svg>}
      {onSite ? t('assignment.accessOnSite', 'On-site') : t('assignment.accessRemote', 'Remote')}
      {inferred && <span aria-hidden>?</span>}
    </span>
  )
}

/**
 * One column template, used by the header row and by every body row.
 *
 * These are separate grid containers, so an `fr` template alone does not align them: `fr`
 * distributes the space left over after each track's min-content, and the action column's
 * min-content (the Assign button plus six icons) is far wider than its fr share. The header,
 * whose last cell is just the word "Status", resolved its tracks differently and the columns
 * drifted apart. minmax(0,…) removes min-content from the calculation and the action column
 * is pinned to the width its contents actually need.
 */
const COLS = 'grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)_196px]'

export function DependencyOrderView({ rows, locale, onAssign, actions, allowBypass = false }: {
  rows: RecoverRequest[]
  locale: string
  onAssign: (row: RecoverRequest) => void
  /** The Queue's per-row actions, so a row means the same thing on every view. */
  actions?: RowActionHandlers
  /** The screen's bypass policy. Without it this view read the DATABASE's answer, which assumes
   *  bypasses are permitted — so it showed Go where the queue, under the same toggle, said Wait. */
  allowBypass?: boolean
}) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const workforce = useWorkforce()

  const ordered = useMemo(() => [...rows].sort(byDependencyOrder), [rows])
  const selected = useMemo(() => ordered.find((r) => r.id === selectedId) ?? null, [ordered, selectedId])
  const { candidates, access, eligibleCount, poolCount } = useCandidates(selected, workforce.data)

  const when = (v: string | Date | null | undefined) => {
    if (!v) return '—'
    try { return formatDateForLocale(typeof v === 'string' ? v : v.toISOString(), locale,
      { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) }
    catch { return '—' }
  }

  return (
    <div className="flex gap-4">
      <div className="min-w-0 flex-1 overflow-hidden rounded-xl border bg-card">
        <div className={cn('grid border-b bg-muted/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground', COLS)}>
          <div className="px-3 py-2.5">{t('recoverRequests.objectID', 'Object')}</div>
          <div className="px-2 py-2.5">{t('recoverRequests.location', 'Location')}</div>
          <div className="px-2 py-2.5">{t('recoverRequests.team', 'Team')}</div>
          <div className="px-2 py-2.5">{t('assignment.access', 'Access')}</div>
          <div className="px-2 py-2.5">{t('recoverRequests.forecastStartTime', 'Forecast start')}</div>
          <div className="px-2 py-2.5">{t('recoverRequests.status', 'Status')}</div>
        </div>

        {ordered.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {t('assignment.queueEmpty',
              'Nothing is waiting for an engineer. Objects leave this queue as soon as they are assigned.')}
          </div>
        )}

        {ordered.map((r) => {
          const isSel = r.id === selectedId
          // The screen's bypass policy, not the database's assumption — same rule the queue uses.
          const go = effectiveIsGo(r, allowBypass)
          const byp = Number(r.bypassableOutstanding ?? 0)
          return (
            <button
              key={`${r.id}-${r.objectID}`}
              type="button"
              onClick={() => setSelectedId(isSel ? null : r.id)}
              className={cn(
                'grid w-full items-center border-b text-left last:border-0 hover:bg-muted/40', COLS,
                isSel && 'bg-muted/50 ring-1 ring-inset ring-primary',
              )}
            >
              <span className="min-w-0 px-3 py-2.5">
                <span className="block truncate text-[13px] font-semibold">{r.objectID}</span>
                <span className="block truncate text-[11px] text-muted-foreground">{r.objectDescription}</span>
              </span>
              <span className="truncate px-2 py-2.5 text-xs">{r.location ?? '—'}</span>
              <span className={cn('truncate px-2 py-2.5 text-xs', !r.teamName && 'text-muted-foreground')}>
                {r.teamName || '—'}
              </span>
              <span className="px-2 py-2.5"><AccessChip {...accessRequirement(r, workforce.data?.accessByObject)} /></span>
              <span className="px-2 py-2.5 text-xs tabular-nums">{when(r.forecastStartTime)}</span>
              <span className="flex items-center justify-start gap-1 px-2 py-2">
                <span className="relative inline-flex">
                  {byp > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums text-white ring-2 ring-background"
                          style={{ backgroundColor: 'rgb(245,158,11)' }}>{byp}</span>
                  )}
                  <span
                    onClick={(e) => { e.stopPropagation(); if (go) onAssign(r) }}
                    className="flex h-12 w-12 items-center justify-center rounded text-xs font-semibold text-white"
                    style={{ backgroundColor: go ? 'rgb(151,200,100)' : 'rgb(232,96,55)' }}
                  >
                    {go ? t('recoverRequests.assign', 'Assign') : 'WAIT'}
                  </span>
                </span>
                {actions && <RowActions row={r} handlers={actions} />}
              </span>
            </button>
          )
        })}
      </div>

      {/* Selection rail. Nothing is preselected — the rail is empty until a row is chosen, and it
          says so, rather than showing a ranking for an object nobody asked about. */}
      <aside className="w-[360px] flex-none self-start overflow-hidden rounded-xl border bg-card">
        <div className="border-b px-3.5 py-3">
          <div className="text-sm font-semibold">{t('assignment.whoCouldTake', 'Who could take this?')}</div>
          {selected
            ? <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{selected.objectID}</div>
            : <div className="mt-0.5 text-[11px] text-muted-foreground">
                {t('assignment.pickARow', 'Choose a row to see candidates.')}
              </div>}
        </div>

        {selected && (
          <>
            {access === 'on-site' && (
              <div className="border-b px-3.5 py-2.5 text-[11.5px] leading-snug"
                   style={{ backgroundColor: 'rgba(245,158,11,0.08)', color: 'rgb(146,88,8)' }}>
                {t('assignment.onSiteBanner',
                  'On-site access required — pool limited to {{site}} and travel range: {{eligible}} of {{pool}} available engineers eligible.',
                  { site: selected.location ?? t('assignment.thisSite', 'this site'), eligible: eligibleCount, pool: poolCount })}
              </div>
            )}

            <div className="flex flex-col gap-1.5 p-2.5">
              {workforce.isLoading && (
                <div className="px-1 py-6 text-center text-xs text-muted-foreground">{t('common.loading', 'Loading…')}</div>
              )}
              {!workforce.isLoading && candidates.length === 0 && (
                <div className="px-1 py-6 text-center text-xs text-muted-foreground">
                  {t('assignment.noEngineers', 'No engineers are recorded against any site yet.')}
                </div>
              )}
              {candidates.map((c, i) => (
                <CandidateCard key={c.engineer.id} candidate={c} rank={i + 1} />
              ))}
            </div>

            {candidates.length > 0 && (
              <div className="border-t px-3.5 py-2.5">
                <button
                  type="button"
                  onClick={() => onAssign(selected)}
                  className="h-8 w-full rounded-lg text-xs font-semibold text-white"
                  style={{ backgroundColor: 'hsl(221 83% 53%)' }}
                >
                  {t('assignment.openAssign', 'Assign an engineer…')}
                </button>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  )
}
